import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Layout components
import { Header, Footer, AnalyticsTracker } from './components';
import { SEO } from './components/SEO';

// Pages
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';

// Styles
import { styles } from './styles/theme';

const lazyNamedPage = (loader, exportName) =>
  lazy(() => loader().then((module) => ({ default: module[exportName] || module.default })));

const BetaProgramPage = lazyNamedPage(() => import('./pages/BetaProgramPage'), 'BetaProgramPage');
const CareersPage = lazyNamedPage(() => import('./pages/CareersPage'), 'CareersPage');
const SupportPage = lazyNamedPage(() => import('./pages/SupportPage'), 'SupportPage');
const PromoPage = lazyNamedPage(() => import('./pages/PromoPage'), 'PromoPage');
const SchedulerPage = lazyNamedPage(() => import('./pages/SchedulerPage'), 'SchedulerPage');
const BlogCmsPage = lazyNamedPage(() => import('./pages/BlogCmsPage'), 'BlogCmsPage');
const OutreachPage = lazyNamedPage(() => import('./pages/OutreachPage'), 'OutreachPage');
const UGCBriefPage = lazyNamedPage(() => import('./pages/UGCBriefPage'), 'UGCBriefPage');
const UGCSuccessPage = lazyNamedPage(() => import('./pages/UGCSuccessPage'), 'UGCSuccessPage');
const ProPage = lazyNamedPage(() => import('./pages/ProPage'), 'ProPage');
const ReferralPage = lazyNamedPage(() => import('./pages/ReferralPage'), 'ReferralPage');
const NotFoundPage = lazyNamedPage(() => import('./pages/NotFoundPage'), 'NotFoundPage');
const Privacy = lazyNamedPage(() => import('./pages/Privacy'), 'default');
const Terms = lazyNamedPage(() => import('./pages/Terms'), 'default');
const routeFallback = <div style={{ minHeight: '60vh' }} />;

function App() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPromoPath = pathname === '/promo' || pathname.startsWith('/promo/');
  const isPromoExperience = hostname === 'promo.bhol.app' || isPromoPath;
  const isMarketingPath = pathname === '/marketing' || pathname.startsWith('/marketing/');
  const isMarketingExperience = hostname === 'marketing.bhol.app' || isMarketingPath;
  const isSchedulePath = pathname === '/schedule' || pathname.startsWith('/schedule/');
  const isScheduleExperience = !isMarketingExperience && (hostname === 'schedule.bhol.app' || isSchedulePath);
  const isUGCPath = pathname === '/ugc' || pathname.startsWith('/ugc/');
  const isUGCExperience = isUGCPath;
  const isBlogCmsPath = pathname === '/cms/blog' || pathname.startsWith('/cms/blog/');

  if (isBlogCmsPath) {
    return (
      <Router>
        <Suspense fallback={routeFallback}>
          <Routes>
            <Route path="*" element={<BlogCmsPage />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  if (isPromoExperience) {
    return (
      <div style={styles.container}>
        <Router>
          <AnalyticsTracker />
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="*" element={<PromoPage />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    );
  }

  if (isScheduleExperience) {
    return (
      <div style={styles.container}>
        <Router>
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="*" element={<SchedulerPage />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    );
  }

  if (isMarketingExperience) {
    return (
      <div style={styles.container}>
        <Router>
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="/marketing/schedule/*" element={<SchedulerPage surfaceLabel="marketing.bhol.app" />} />
              <Route path="/schedule/*" element={<SchedulerPage surfaceLabel="marketing.bhol.app" />} />
              <Route path="*" element={<OutreachPage />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    );
  }

  if (isUGCExperience) {
    return (
      <div style={styles.container}>
        <Router>
          <AnalyticsTracker />
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="/ugc/success" element={<UGCSuccessPage />} />
              <Route path="*" element={<UGCBriefPage />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <SEO />
      <Router>
        <AnalyticsTracker />
        <Header />

        <main style={styles.main}>
          <Suspense fallback={routeFallback}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/beta" element={<BetaProgramPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/promo" element={<PromoPage />} />
              <Route path="/ugc" element={<UGCBriefPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/pro" element={<ProPage />} />
              <Route path="/j/:code" element={<ReferralPage />} />
              <Route path="/j" element={<ReferralPage />} />
              <Route path="/r/:code" element={<ReferralPage />} />
              <Route path="/r" element={<ReferralPage />} />
              {/* /blog and /blog/:slug are served by Pages Functions (SSR from R2) */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>

        <Footer />
      </Router>
    </div>
  );
}

export default App;
